import { z } from 'zod';

import { FlightSegmentSchema } from './flight-schemas.js';
import { BookingStatusSchema } from './itinerary-item-schemas.js';
import { CostSchema } from './shared-schemas.js';

/**
 * Event types for itinerary display
 * These represent individual events that are displayed to users,
 * expanded from the underlying itinerary items
 */
export const ItineraryItemEventTypeSchema = z.enum([
  'flight_departure',
  'flight_arrival',
  'layover',
  'hotel_checkin',
  'hotel_checkout',
  'activity',
  'custom',
  'place',
  'place_dining',
  'place_shopping',
  'place_nature',
  'place_culture',
  'place_entertainment',
  'place_wellness',
  'place_nightlife',
  'destination_arrival',
]);

export type ItineraryItemEventType = z.infer<typeof ItineraryItemEventTypeSchema>;

/**
 * Metadata schemas for different event types
 */
export const FlightEventMetadataSchema = z.object({
  segment: FlightSegmentSchema,
  duration: z.number().optional(),
  /**
   * True on the earliest `flight_departure` event of the item's OUTBOUND leg only (never on a
   * `returnSegments` leg — a round trip books as one item, from the outbound card). Booking status
   * is attached to every leg of a multi-segment flight, but a primary action like "Book" should
   * still appear on only one card — this flags which one. Absent/false on every other leg.
   */
  isFirstSegment: z.boolean().optional(),
});

/**
 * Base schema for all itinerary item events
 */
const BaseItineraryItemEventSchema = z.object({
  /** Unique identifier for this event (e.g., "item123__departure_0") */
  id: z.string(),

  /** Display title for the event (e.g., "Departure from JFK") */
  title: z.string(),

  /** ISO datetime string for when this event occurs */
  dateTime: z.string(),

  /** Reference to the original itinerary item ID */
  itemId: z.string().describe('ID of the source itinerary item'),

  /** Tiebreaker for events at the same time. Lower values sort first. */
  sortPriority: z.number().optional(),

  /**
   * Display cost for this event, set by the expansion engine so clients don't re-derive it.
   * A multi-segment flight's (whole-flight) cost is attached to its first segment only; other
   * segments omit it. Absent when the source item has no resolvable cost.
   */
  cost: CostSchema.optional(),

  /**
   * Booking status for this event, set by the expansion engine. For a multi-room hotel check-in it's the
   * aggregate across rooms (planned wins — see `aggregateBookingStatus`); for other items it's the item's
   * own status, attached to EVERY event the item expands to (each leg of a multi-segment flight, each day
   * of a multi-day item) — unlike `cost`, which is attached once. Absent when there's no status to show.
   */
  bookingStatus: BookingStatusSchema.optional(),
});

/**
 * ItineraryItemEvent Schema with discriminated unions for type-safe metadata
 * Represents a single displayable event in the itinerary view.
 * These are ephemeral objects created on-demand from ItineraryItem documents.
 */
export const ItineraryItemEventSchema = z.discriminatedUnion('type', [
  BaseItineraryItemEventSchema.extend({ type: z.literal('flight_departure'), metadata: FlightEventMetadataSchema }),
  BaseItineraryItemEventSchema.extend({ type: z.literal('flight_arrival'), metadata: FlightEventMetadataSchema }),
  BaseItineraryItemEventSchema.extend({ type: z.literal('layover'), metadata: FlightEventMetadataSchema }),
  BaseItineraryItemEventSchema.extend({ type: z.literal('destination_arrival'), metadata: FlightEventMetadataSchema }),
  BaseItineraryItemEventSchema.extend({
    type: z.enum([
      'hotel_checkin',
      'hotel_checkout',
      'activity',
      'custom',
      'place',
      'place_dining',
      'place_shopping',
      'place_nature',
      'place_culture',
      'place_entertainment',
      'place_wellness',
      'place_nightlife',
    ]),
    metadata: z.record(z.string(), z.unknown()).optional(),
  }),
]);

export type ItineraryItemEvent = z.infer<typeof ItineraryItemEventSchema>;

/**
 * A single canonical calendar day in a trip's itinerary, computed server-side
 * by the shared expansion engine. `dateKey` is a local YYYY-MM-DD key, matching
 * how events are grouped for display; `dayNumber` is the 1-based "Day N" label;
 * `isTravelDay` marks a day spent in transit (no events, previous day ends on a
 * flight departure).
 */
export const ItineraryDaySchema = z.object({
  dateKey: z.string().describe('Local calendar-date key (YYYY-MM-DD)'),
  dayNumber: z.number().int().describe('1-based sequential day number for "Day N" labels'),
  isTravelDay: z.boolean().describe('True when the traveler is in transit on this day'),
});

export type ItineraryDay = z.infer<typeof ItineraryDaySchema>;
